import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Microphone, Stop, Waveform, FileText, CurrencyDollar, Receipt, Lightning, SkipForward, Pause, Play, Prohibit, PencilSimple, Plus, Trash, Keyboard, MapPin, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { GlobalSearch } from "@/components/GlobalSearch";
import { WalkthroughTour } from "@/components/WalkthroughTour";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";

const STAGES = { idle: "", transcribing: "Transcribing your recording…", parsing: "Understanding the job…" };
const SAMPLE_JOBS = [
  { id: "panel", label: "Electrical panel", text: "Replaced a 200-amp electrical panel and four outlets at the Miller house on Oak Street. Supplied the panel and breakers. On site about five hours." },
  { id: "pipe", label: "Emergency pipe repair", text: "Emergency call for a burst supply line in the basement at 18 Pine Ave. Cut out the bad section, sweated in copper, and tested the repair. Two hours plus the coupling and fittings." },
  { id: "hvac", label: "HVAC service", text: "HVAC service call. Replaced a dual-run capacitor and added two pounds of refrigerant on the rooftop unit at Daily Double. Customer supplied nothing." },
];

export default function Home() {
  const nav = useNavigate();
  const [recording, setRecording] = useState(false);
  const [stage, setStage] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [biz, setBiz] = useState({ business_name: "Job Spoken" });
  const [showText, setShowText] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [draftText, setDraftText] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const [s, j, b] = await Promise.all([api.get("/stats"), api.get("/jobs"), api.get("/settings")]);
      setStats(s.data); setJobs(j.data.slice(0, 4)); setBiz(b.data);
      if (!b.data.business_zip || !b.data.business_name) nav("/onboarding");
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 402) return;
      console.error("Home load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); handleUpload(); };
      mr.start();
      mediaRef.current = mr;
      setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Please allow mic permission.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    mediaRef.current && mediaRef.current.stop();
  };

  const handleUpload = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 800) { toast.error("Recording too short. Try again."); return; }
    const fd = new FormData();
    fd.append("audio", blob, "work.webm");
    try {
      setStage("transcribing");
      const tr = await api.post("/transcribe", fd);
      const text = tr.data.text?.trim();
      if (!text) { toast.error("Couldn't hear anything. Try again."); setStage("idle"); return; }
      setDraftText(text);
      setVoiceMode(true);
      setShowText(true);
      setStage("idle");
    } catch (e) {
      toast.error("Something went wrong processing audio.");
      setStage("idle");
    }
  };

  const processText = async () => {
    const text = draftText.trim();
    if (text.length < 6) { toast.error("Please describe the work first."); return; }
    try {
      setStage("parsing");
      const job = await api.post("/jobs/parse", { transcript: text });
      toast.success("Job captured");
      setShowText(false); setDraftText(""); setStage("idle");
      nav(`/job/${job.data.id}`);
    } catch (e) {
      toast.error("Couldn't understand that. Please try again.");
      setStage("idle");
    }
  };

  const busy = stage !== "idle";
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-7 pb-4">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">On the clock</p>
        <h1 className="font-heading text-3xl font-black text-zinc-900 tracking-tight">{biz.business_name || "Job Spoken"}</h1>
      </header>

      {/* Stats */}
      <div className="px-5 grid grid-cols-3 gap-3">
        <Stat label="Jobs Today" value={stats?.jobs_today ?? "–"} testid="stat-jobs-today" />
        <Stat label="Outstanding" value={stats ? money(stats.outstanding) : "–"} testid="stat-outstanding" money />
        <Stat label="Invoices" value={stats?.total_invoices ?? "–"} testid="stat-invoices" />
      </div>

      <div className="px-5 mt-3 space-y-3">
        <GlobalSearch />
        <WalkthroughTour />
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="home-scan-receipt-btn" onClick={() => nav("/receipt/scan")}
            className="h-12 rounded-xl bg-zinc-900 text-white font-bold flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform text-sm">
            <Receipt size={18} weight="fill" /> Receipt
          </button>
          <button data-testid="home-properties-btn" onClick={() => nav("/properties")}
            className="h-12 rounded-xl bg-white border border-zinc-200 text-zinc-800 font-bold flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform text-sm">
            <MapPin size={18} weight="bold" /> Properties
          </button>
          <button data-testid="home-stock-btn" onClick={() => nav("/stock")}
            className="h-12 rounded-xl bg-white border border-zinc-200 text-zinc-800 font-bold flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform text-sm">
            <Package size={18} weight="bold" /> Inventory
          </button>
        </div>
      </div>

      <RevenueCard />
      <TipsCard />
      <RecurringCard nav={nav} />

      {/* Mic hero */}
      <div className="flex flex-col items-center justify-center py-10">
        <div className="relative flex items-center justify-center">
          {recording && <span className="absolute w-44 h-44 rounded-full bg-orange-500/40 pulse-ring" />}
          <button
            data-testid="record-button"
            disabled={busy}
            onClick={recording ? stopRecording : startRecording}
            className={`relative w-44 h-44 rounded-full flex flex-col items-center justify-center text-white shadow-[0_10px_30px_rgba(234,88,12,0.45)] active:translate-y-0.5 transition-transform disabled:opacity-70 ${
              recording ? "bg-red-600" : "bg-orange-600"
            }`}
          >
            {busy ? (
              <Waveform size={54} weight="fill" className="animate-pulse" />
            ) : recording ? (
              <Stop size={50} weight="fill" />
            ) : (
              <Microphone size={58} weight="fill" />
            )}
            <span className="mt-1 font-heading font-bold text-sm">
              {busy ? "Working…" : recording ? mm + ":" + ss : "Record"}
            </span>
          </button>
        </div>
        <p className="mt-6 text-center text-zinc-500 text-sm max-w-[16rem] leading-relaxed" data-testid="record-hint">
          {busy ? STAGES[stage] : recording ? "Tap to stop when you're done describing the job." : "Tap and describe today's work in plain words."}
        </p>
        <button
          data-testid="type-work-btn"
          disabled={busy || recording}
          onClick={() => { setDraftText(""); setVoiceMode(false); setShowText(true); }}
          className="mt-5 text-xs tracking-[0.12em] uppercase font-bold text-zinc-500 border border-zinc-200 rounded-full px-5 h-11 flex items-center gap-2 active:scale-95 transition-transform disabled:opacity-40"
        >
          <Keyboard size={17} weight="bold" /> Type Work Instead
        </button>
      </div>

      {/* Recent jobs */}
      <div className="px-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Recent Jobs</h2>
        </div>
        {jobs.length === 0 && (
          <div className="text-sm text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-xl" data-testid="empty-jobs">
            No jobs yet. Record your first one above.
          </div>
        )}
        <div className="space-y-2.5">
          {jobs.map((j) => (
            <button
              key={j.id}
              data-testid={`recent-job-${j.id}`}
              onClick={() => nav(j.invoice_id ? `/invoice/${j.invoice_id}` : (j.won_at && !j.schedule?.start ? `/job/${j.id}?schedule=1` : `/job/${j.id}`))}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                {j.invoice_id ? <Receipt size={22} weight="bold" /> : <FileText size={22} weight="bold" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-zinc-900 truncate">{j.customer_name || "Unnamed job"}</p>
                <p className="text-xs text-zinc-500 truncate">{j.location || "—"} · {j.tasks?.length || 0} tasks</p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${j.invoice_id ? "bg-zinc-900 text-white" : j.won_at ? "bg-green-600 text-white" : j.estimate_id ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>
                {j.invoice_id ? "Invoiced" : j.won_at ? "Won" : j.estimate_id ? "Estimate" : "Draft"}
              </span>
            </button>
          ))}
        </div>
      </div>

      <Drawer open={showText} onOpenChange={(o) => { if (!o && stage !== "parsing") setShowText(false); }}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">{voiceMode ? "Review & edit transcription" : "Describe the work"}</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-2 space-y-3">
            <p className="text-sm text-zinc-500">{voiceMode ? "Edit the transcription if needed, then process it." : "Type today's work in plain words. It runs through the same AI as voice."}</p>
            {!voiceMode && (
              <div className="flex flex-wrap gap-2">
                {SAMPLE_JOBS.map((sample) => (
                  <button key={sample.label} type="button" data-testid={`sample-${sample.id}`} onClick={() => setDraftText(sample.text)}
                    className="text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 text-zinc-700 bg-white">
                    {sample.label}
                  </button>
                ))}
              </div>
            )}
            <Textarea
              data-testid="work-text-input"
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows={6}
              placeholder="e.g. Worked at Daily Double today. Replaced men's toilet fill valve, cleared west rooftop AC drain and replaced two kitchen outlets. I supplied all parts."
              className="resize-none text-base"
            />
          </div>
          <DrawerFooter>
            <Button data-testid="process-job-btn" onClick={processText} disabled={stage === "parsing"} className="h-12 font-bold bg-orange-600 hover:bg-orange-700">
              {stage === "parsing" ? "Understanding the job…" : "Process Job"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid={testid}>
    <p className="text-[10px] tracking-[0.12em] uppercase font-bold text-zinc-400">{label}</p>
    <p className="font-heading font-black text-lg text-zinc-900 mt-1 truncate">{value}</p>
  </div>
);

const RevenueCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/stats/monthly").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const hasData = (d.paid + d.outstanding) > 0;
  return (
    <div className="px-5 mt-4">
      <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="revenue-card">
        <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400 mb-3">Cash Flow · Last 6 Months</h2>
        <div className="flex gap-6 mb-3">
          <div><p className="text-[10px] uppercase font-bold text-zinc-400">Collected</p><p className="font-heading font-black text-lg text-green-600" data-testid="rev-paid">{money(d.paid)}</p></div>
          <div><p className="text-[10px] uppercase font-bold text-zinc-400">Outstanding</p><p className="font-heading font-black text-lg text-orange-600" data-testid="rev-outstanding">{money(d.outstanding)}</p></div>
        </div>
        {hasData ? (
          <div style={{ width: "100%", height: 160 }} data-testid="revenue-chart">
            <ResponsiveContainer>
              <BarChart data={d.months} barGap={4}>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#f4f4f5" }} />
                <Bar dataKey="invoiced" fill="#e4e4e7" radius={[4, 4, 0, 0]} />
                <Bar dataKey="collected" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-sm text-zinc-400 py-4 text-center">No revenue yet — send your first invoice.</p>}
      </div>
    </div>
  );
};

const TipFig = ({ label, value, testid }) => (
  <div data-testid={testid}><p className="text-[10px] uppercase font-bold text-zinc-400">{label}</p><p className="font-heading font-black text-base text-teal-600 mt-0.5">{money(value)}</p></div>
);

const TipsCard = () => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/stats/tips").then((r) => setT(r.data)).catch(() => {}); }, []);
  if (!t) return null;
  const any = (t.today + t.week + t.month + t.year) > 0;
  return (
    <div className="px-5 mt-4">
      <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="tips-card">
        <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400 mb-3">Tips Received</h2>
        <div className="grid grid-cols-4 gap-2 text-center">
          <TipFig label="Today" value={t.today} testid="tips-today" />
          <TipFig label="Week" value={t.week} testid="tips-week" />
          <TipFig label="Month" value={t.month} testid="tips-month" />
          <TipFig label="Year" value={t.year} testid="tips-year" />
        </div>
        {!any && <p className="text-xs text-zinc-400 mt-2 text-center">No tips yet — customers can add one at checkout.</p>}
      </div>
    </div>
  );
};

const FREQ_OPTS = [["weekly", "Weekly"], ["biweekly", "Every 2 weeks"], ["monthly", "Monthly"], ["bimonthly", "Every 2 months"], ["quarterly", "Quarterly"], ["semiannual", "Every 6 months"], ["annual", "Annually"], ["custom", "Custom (days)"]];
const FREQ_LABEL = { weekly: "Weekly", biweekly: "Every 2 wks", monthly: "Monthly", bimonthly: "Every 2 mo", quarterly: "Quarterly", semiannual: "Every 6 mo", annual: "Annually", custom: "Custom" };
const Chip = ({ onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex items-center gap-1 text-xs font-semibold border border-zinc-200 rounded-lg px-2.5 h-8 text-zinc-700 active:scale-95 transition-transform">
    <Icon size={14} weight="bold" /> {label}
  </button>
);

const RecurringCard = ({ nav }) => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/recurring").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      const r = await api.post(`/recurring/${id}/action`, { action });
      if (action === "generate" && r.data.invoice_id) { toast.success("Draft invoice created"); nav(`/invoice/${r.data.invoice_id}`); return; }
      toast.success("Updated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const openEdit = (r) => setEdit({ ...r, tasks: (r.tasks || []).map((t) => ({ ...t })) });
  const setTask = (i, patch) => setEdit((e) => { const tasks = [...e.tasks]; tasks[i] = { ...tasks[i], ...patch }; return { ...e, tasks }; });
  const addTask = () => setEdit((e) => ({ ...e, tasks: [...e.tasks, { description: "New task", quantity: 1, unit_price: 0, amount: 0 }] }));
  const rmTask = (i) => setEdit((e) => ({ ...e, tasks: e.tasks.filter((_, idx) => idx !== i) }));
  const saveEdit = async () => {
    const body = { ...edit, tasks: edit.tasks.map((t) => { const price = Number(t.amount || t.unit_price || 0); return { ...t, quantity: 1, unit_price: price, amount: price, accepted: true }; }) };
    try { await api.put(`/recurring/${edit.id}`, body); setEdit(null); toast.success("Recurring template updated"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Save failed"); }
  };

  if (list.length === 0) return null;
  return (
    <div className="px-5 mt-4">
      <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400 mb-3">Upcoming Recurring Work</h2>
      <div className="space-y-2.5" data-testid="recurring-list">
        {list.map((r) => (
          <div key={r.id} data-testid={`recurring-${r.id}`} className="bg-white border border-zinc-200 rounded-xl p-3.5">
            <p className="font-semibold text-zinc-900 truncate">{r.customer_name || "—"}</p>
            <p className="text-xs text-zinc-500 truncate">{r.location || "—"} · {r.service_description}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5">Next: {r.next_date} · {FREQ_LABEL[r.frequency] || r.frequency}{r.status === "paused" ? " · Paused" : ""}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Chip onClick={() => act(r.id, "generate")} icon={Lightning} label="Generate now" testid={`recur-generate-${r.id}`} />
              <Chip onClick={() => openEdit(r)} icon={PencilSimple} label="Edit" testid={`recur-edit-${r.id}`} />
              <Chip onClick={() => act(r.id, "skip")} icon={SkipForward} label="Skip" testid={`recur-skip-${r.id}`} />
              {r.status === "paused"
                ? <Chip onClick={() => act(r.id, "resume")} icon={Play} label="Resume" testid={`recur-resume-${r.id}`} />
                : <Chip onClick={() => act(r.id, "pause")} icon={Pause} label="Pause" testid={`recur-pause-${r.id}`} />}
              <Chip onClick={() => act(r.id, "end")} icon={Prohibit} label="End" testid={`recur-end-${r.id}`} />
            </div>
          </div>
        ))}
      </div>

      <Drawer open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DrawerContent className="max-w-md mx-auto max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">Edit Recurring Template</DrawerTitle></DrawerHeader>
          {edit && (
            <div className="px-4 pb-4 overflow-y-auto space-y-3" data-testid="recur-edit-drawer">
              <div>
                <label className="text-[11px] uppercase font-bold text-zinc-400">Service description</label>
                <Input data-testid="recur-edit-service" value={edit.service_description || ""} onChange={(e) => setEdit({ ...edit, service_description: e.target.value })} className="h-11 mt-1" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] uppercase font-bold text-zinc-400">Tasks & default prices</label>
                  <button data-testid="recur-edit-add-task" onClick={addTask} className="text-xs font-bold text-orange-600 flex items-center gap-1"><Plus size={14} weight="bold" /> Add</button>
                </div>
                <div className="space-y-2">
                  {edit.tasks.map((t, i) => (
                    <div key={i} className="flex items-start gap-2" data-testid={`recur-edit-task-${i}`}>
                      <Textarea data-testid={`recur-edit-task-desc-${i}`} value={t.description || ""} onChange={(e) => setTask(i, { description: e.target.value })} rows={2} className="flex-1 resize-none text-sm" />
                      <Input data-testid={`recur-edit-task-price-${i}`} type="number" value={t.amount ?? t.unit_price ?? 0} onChange={(e) => setTask(i, { amount: Number(e.target.value) })} className="h-9 w-24" />
                      <button data-testid={`recur-edit-task-del-${i}`} onClick={() => rmTask(i)} className="text-zinc-300 hover:text-red-500 pt-1"><Trash size={18} /></button>
                    </div>
                  ))}
                  {edit.tasks.length === 0 && <p className="text-xs text-zinc-400">No tasks — add at least one so invoices can be generated.</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase font-bold text-zinc-400">Frequency</label>
                  <select data-testid="recur-edit-frequency" value={edit.frequency} onChange={(e) => setEdit({ ...edit, frequency: e.target.value })} className="h-11 w-full border border-zinc-200 rounded-lg px-2 bg-white mt-1">
                    {FREQ_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {edit.frequency === "custom" && (
                  <div><label className="text-[11px] uppercase font-bold text-zinc-400">Every N days</label>
                    <Input data-testid="recur-edit-custom-days" type="number" value={edit.custom_days || 0} onChange={(e) => setEdit({ ...edit, custom_days: Number(e.target.value) })} className="h-11 mt-1" /></div>
                )}
                <div><label className="text-[11px] uppercase font-bold text-zinc-400">Next date</label>
                  <Input data-testid="recur-edit-next" type="date" value={edit.next_date || ""} onChange={(e) => setEdit({ ...edit, next_date: e.target.value })} className="h-11 mt-1" /></div>
                <div><label className="text-[11px] uppercase font-bold text-zinc-400">End date (optional)</label>
                  <Input data-testid="recur-edit-end" type="date" value={edit.end_date || ""} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} className="h-11 mt-1" /></div>
                <div><label className="text-[11px] uppercase font-bold text-zinc-400">Max occurrences</label>
                  <Input data-testid="recur-edit-max" type="number" value={edit.max_occurrences || 0} onChange={(e) => setEdit({ ...edit, max_occurrences: Number(e.target.value) })} className="h-11 mt-1" /></div>
              </div>
              <p className="text-xs text-zinc-400">Changes apply to future generated invoices only — past invoices are never modified.</p>
            </div>
          )}
          <DrawerFooter><Button data-testid="recur-edit-save" onClick={saveEdit} className="h-12 font-bold">Save Template</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
